import React, { createContext, useContext, useEffect, useMemo, useState } from "react";

/**
 * Security Store (members/security/Security_store.js)
 * - Persists keyId (GUID now, Cognito later), sessionToken, userName
 * - Placeholder validateUserKeyId() for future API/Cognito validation
 */

const STORAGE_KEY = "membership.security.v1";

const SecurityContext = createContext(null);

export function SecurityProvider({ children }) {
  const [keyId, setKeyId] = useState("");
  const [sessionToken, setSessionToken] = useState(""); // e.g., Cognito/JWT later
  const [userName, setUserName] = useState("");

  // hydrate once
  useEffect(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) {
        const { keyId: k, sessionToken: t, userName: u } = JSON.parse(raw);
        if (k) setKeyId(k);
        if (t) setSessionToken(t);
        if (u) setUserName(u);
      }
    } catch (_) {}
  }, []);

  // persist on change
  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ keyId, sessionToken, userName }));
  }, [keyId, sessionToken, userName]);

  /**
   * Placeholder validator: swap with real API/Cognito.
   * For now, accept any non-empty keyId and mint a fake token.
   */
  async function validateUserKeyId(inputKeyId) {
    if (!inputKeyId) return { ok: false, reason: "Missing keyId" };

    // TODO: Replace with real call:
    // const resp = await fetch(`/api/security/validate?keyId=${encodeURIComponent(inputKeyId)}`, { credentials: 'include' });
    // const json = await resp.json(); // { ok: true/false, token: '...', userName: '...' }

    const fakeToken = `TEST_TOKEN_${inputKeyId}`;
    setKeyId(inputKeyId);
    setSessionToken(fakeToken);
    return { ok: true, token: fakeToken };
  }

  function clearSecurity() {
    setKeyId("");
    setSessionToken("");
    setUserName("");
    localStorage.removeItem(STORAGE_KEY);
  }

  const value = useMemo(
    () => ({
      keyId,
      sessionToken,
      userName,
      setKeyId,
      setSessionToken,
      setUserName,
      validateUserKeyId,
      clearSecurity,
    }),
    [keyId, sessionToken, userName]
  );

  return <SecurityContext.Provider value={value}>{children}</SecurityContext.Provider>;
}

export function useSecurity() {
  const ctx = useContext(SecurityContext);
  if (!ctx) throw new Error("useSecurity must be used within <SecurityProvider>");
  return ctx;
}